import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useCurrentProvider } from '../../Components/Permissions';
import { ActiveWeek } from '../../Components/Scheduler/ActiveWeek';
import { CalendarRangePicker } from '../../Components/Scheduler/CalendarRangePicker';
import { PaddedPage } from '../../globalStyles';
import { useAdminAppointmentsQuery } from '../../graphQL';
import { TimeZone, getWeek, guessTz } from '../../utils';
import { PageTitle, UnexpectedError } from '../Shared';
import { AppointmentList } from './AppointmentList';
import * as Styles from './styles';
import { Appointment } from './types';

export function AppointmentsPage() {
  const { currentProvider } = useCurrentProvider();
  const [startDate, setStartDate] = useState(moment().startOf('week'));
  const [timezone, setTimezone] = useState<TimeZone>(guessTz());
  const [selectedProviderId, setSelectedProviderId] = useState<number | undefined>(
    currentProvider.role === 'provider' ? currentProvider.id : undefined
  );
  const [showAllProviders, setShowAllProviders] = useState(false);

  const { data, refetch, error } = useAdminAppointmentsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      startTime: startDate.clone().startOf('week').toDate(),
      endTime: startDate.clone().endOf('week').toDate(),
      providerId: selectedProviderId,
      allProviders: showAllProviders,
    },
    skip: !selectedProviderId && !showAllProviders,
  });

  const now = moment();
  const appointments: Appointment[] = useMemo(
    () =>
      data
        ? data.adminAppointments
            .filter(appointment => appointment.status !== 'unknown')
            .map(i => ({
              ...i,
              startMoment: moment(i.startTime).tz(timezone),
              endMoment: moment(i.endTime).tz(timezone),
            }))
        : [],
    [data, timezone]
  );

  const datesWithAppointments = useMemo(() => appointments.map(i => i.startMoment), [appointments]);

  const handleAdvanceWeek = (delta: number) =>
    setStartDate(startDate.clone().startOf('week').add(delta, 'weeks'));

  const handleProviderSelection = (providerId: number | null) => {
    if (providerId === null) {
      setShowAllProviders(false);
      setSelectedProviderId(undefined);
    } else if (providerId === 0) {
      setShowAllProviders(true);
      setSelectedProviderId(0);
    } else {
      setShowAllProviders(false);
      setSelectedProviderId(providerId);
    }
  };

  return (
    <PaddedPage>
      <Styles.HeaderRow>
        <PageTitle>Appointments</PageTitle>
      </Styles.HeaderRow>
      <Styles.BodyWrapper>
        <CalendarRangePicker
          startDate={startDate}
          onChangeStartDate={setStartDate}
          monthButtonAlignment="left"
          circledDate={now}
          leftMonthOffset={-1}
        />
        <ActiveWeek
          startDate={startDate.clone().startOf('week')}
          onChangeWeek={delta => handleAdvanceWeek(delta)}
          activeDays={getWeek(startDate, { timezone, startOfWeek: true })
            .filter(i => i.isSameOrAfter(now, 'date'))
            .map(i => i.day())}
          highlightedDate={now}
          dottedDates={datesWithAppointments}
          timezone={timezone}
        />
      </Styles.BodyWrapper>
      <Styles.Divider />
      <Styles.BodyWrapper>
        {error && <UnexpectedError />}
        <AppointmentList
          appointments={appointments}
          startDate={startDate}
          endDate={startDate.clone().endOf('week')}
          onAdvanceWeek={() => handleAdvanceWeek(1)}
          timeZone={timezone}
          onChangeTimeZone={setTimezone}
          refetch={refetch}
          selectedProviderId={selectedProviderId}
          setSelectedProviderId={handleProviderSelection}
        />
      </Styles.BodyWrapper>
    </PaddedPage>
  );
}
